import axios from "axios";

const useApi = () => {
  const env = process.env.REACT_APP_ENV || "development";

  const baseURLMapping = {
    production: process.env.REACT_APP_BASE_URL_PRODUCTION,
    staging: process.env.REACT_APP_BASE_URL_STAGING,
    development: process.env.REACT_APP_BASE_URL_DEV, // Default to dev
  };

  const baseURL = `${
    baseURLMapping[env] || process.env.REACT_APP_BASE_URL_DEV
  }/api`;

  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("access")}`,
  };

  const axiosInstance = axios.create({
    baseURL,
    headers,
  });

  // Test connection

  const getTestConnection = async () => {
    try {
      const response = await axiosInstance.get("/test-connection/");
      return response;
    } catch (error) {
      throw error;
    }
  };

  // Common use

  const getCollections = async () => {
    try {
      const response = await axiosInstance.get("/collection/");
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getZones = async (active_only = false, branchId = null) => {
    try {
      if (active_only) {
        const params = branchId ? { branch_id: branchId } : {};
        const response = await axiosInstance.get("/zone/active_zones/", {
          params,
        });
        return response;
      } else {
        const response = await axiosInstance.get("/zone/");
        return response;
      }
    } catch (error) {
      throw error;
    }
  };

  const getLines = async () => {
    try {
      const response = await axiosInstance.get("/line/");
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getZoneConfigs = async () => {
    try {
      const response = await axiosInstance.get("/zone-configuration/");
      return response;
    } catch (error) {
      throw error;
    }
  };

  const createZoneConfig = async (data) => {
    try {
      const response = await axiosInstance.post("/zone-configuration/", data);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const updateZoneConfigs = async (id, data) => {
    try {
      const response = await axiosInstance.patch(`/zone-configuration/${id}/`, {
        params: data,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const patchZoneIsStaff = async (zoneId, isStaff) => {
    const response = await axiosInstance.patch(`/zone/${zoneId}/`, {
      is_staff_zone: isStaff,
    });
    return response.data;
  };

  const patchZoneIsFila = async (zoneId, isFila) => {
    const response = await axiosInstance.patch(`/zone/${zoneId}/`, {
      is_fila_zone: isFila,
    });
    return response.data;
  };

  const updateZoneCoordinates = async (zone) => {
    try {
      const response = await axiosInstance.patch(
        `/zone/update_zone_coordinates/`,
        {
          id: zone.id,
          config_id: zone.configuration,
          camera_id: zone.camera_id,
          coordinates: zone.coordinates,
          description: zone.description,
          is_staff_zone: zone.is_staff_zone,
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const updateLineCoordinates = async (line) => {
    try {
      const response = await axiosInstance.patch(
        `/line/update_line_coordinates/`,
        {
          id: line.id,
          config_id: line.configuration,
          camera_id: line.camera_id,
          coordinates: line.coordinates,
          direction: line.direction,
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const deactivateOtherConfigs = async () => {
    try {
      const response = await axiosInstance.post(
        "/zone-configuration/deactivate-other-configs/"
      );
      return response.data;
    } catch (error) {
      throw new Error("Erro ao desativar configuracoes: " + error.message);
    }
  };

  const removeZoneFromConfig = async (zoneId, configId, cameraId) => {
    try {
      const response = await axiosInstance.post(
        `/zone/remove_zone_from_config/`,
        {
          id: zoneId,
          config_id: configId,
          camera_id: cameraId,
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const removeLineFromConfig = async (lineId, configId, cameraId) => {
    try {
      const response = await axiosInstance.post(
        `/line/remove_line_from_config/`,
        {
          id: lineId,
          config_id: configId,
          camera_id: cameraId,
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const deleteLine = async (id) => {
    try {
      const response = await axiosInstance.delete(`/line/${id}/`);
      return response;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const duplicateConfig = async (configId) => {
    try {
      const response = await axiosInstance.post(
        `/zone-configuration/${configId}/duplicate/`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Verifiers

  const getCheckEventsContent = async () => {
    try {
      const response = await axiosInstance.get(
        "/events-register/check_content/"
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getCheckCollectionsChangeContent = async () => {
    try {
      const response = await axiosInstance.get(
        "/collections-change-register/check_content/"
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getCheckFlowDetectionsContent = async () => {
    try {
      const response = await axiosInstance.get(
        "/flow-detections/check_content/"
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  // FlowDetections

  const getFlowDetectionsLastRecord = async () => {
    try {
      const response = await axiosInstance.get(
        "/flow-detections/get_last_record/"
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getCalibrationStatus = async () => {
    try {
      const response = await axiosInstance.get("/calibration/status/");
      return response;
    } catch (error) {
      return null;
    }
  };

  // CollectionsChange

  const getEventsLastChange = async () => {
    try {
      const response = await axiosInstance.get(
        "events-analysis/get_last_record/"
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  // PeakHours

  // EventsManager
  const postEvent = async (params) => {
    try {
      const response = await axiosInstance.post("/events-register/", {
        params: params,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const patchEvent = async (id, params) => {
    try {
      const response = await axiosInstance.patch(
        `/events-register/${id}/`,
        params
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const deleteEvent = async (id) => {
    try {
      const response = await axiosInstance.delete(`/events-register/${id}/`);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const updateEventGroup = async (eventIds, params) => {
    try {
      const response = await axiosInstance.patch(
        "/events-register/update_group/",
        {
          event_ids: eventIds,
          ...params,
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const deleteEventGroup = async (eventIds) => {
    try {
      const response = await axiosInstance.post(
        "/events-register/delete_group/",
        {
          event_ids: eventIds,
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getEventsList = async (params) => {
    try {
      const response = await axiosInstance.get(
        "/events-register/list_for_edit/",
        {
          params,
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getZoneById = async (id) => {
    try {
      const response = await axiosInstance.get(`/zone/${id}/`);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const patchZoneCollection = async (zoneId, newCollection) => {
    try {
      const collectionIds = newCollection.join(",");

      const response = await axiosInstance.patch(
        `zone/${zoneId}/update_zone_collection/?new_collection=${encodeURIComponent(
          collectionIds
        )}`
      );

      return response;
    } catch (error) {
      throw error;
    }
  };

  const postCollection = async (params) => {
    try {
      const response = await axiosInstance.post("/collection/", {
        params: params,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  // Edit Zones
  const postZones = async (data) => {
    try {
      const response = await axiosInstance.post("/zone/create_zones/", {
        data: data,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const postLines = async (data) => {
    try {
      const response = await axiosInstance.post("/line/create_lines/", {
        data: data,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const postConfigs = async (data) => {
    try {
      const response = await axiosInstance.post("/zone-configuration/", {
        params: data,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getCameras = async () => {
    try {
      const response = await axiosInstance.get("/cameras/");
      return response;
    } catch (error) {
      throw error;
    }
  };

  const updateZonesInCameras = async (data) => {
    try {
      const response = await axiosInstance.patch(
        "/cameras/update_zones_in_cameras/",
        {
          data: data,
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const updateLinesInCameras = async (data) => {
    try {
      const response = await axiosInstance.patch(
        "/cameras/update_lines_in_cameras/",
        { data }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const createCamera = async (data, file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("data", JSON.stringify(data));

    try {
      const response = await axiosInstance.post("/cameras/", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const removeCamera = async (id) => {
    try {
      const response = await axiosInstance.delete(`/cameras/${id}/`);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const editCamera = async (id, data) => {
    const formData = new FormData();
    formData.append("data", JSON.stringify(data));

    try {
      const response = await axiosInstance.patch(`/cameras/${id}/`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const setActiveCamera = async (id, isActive, reason) => {
    try {
      const response = await axiosInstance.post(`/cameras/${id}/set_active/`, {
        is_active: isActive,
        ...(reason ? { reason } : {}),
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getCameraEnablementHistory = async (id) => {
    try {
      const response = await axiosInstance.get(
        `/cameras/${id}/enablement_history/`
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const countCameras = async () => {
    try {
      const response = await axiosInstance.get("/cameras/count/");
      return response;
    } catch (error) {
      throw error;
    }
  };

  const refreshConfigCameraFrames = async (configId, timeoutMs = 240000) => {
    try {
      const response = await axiosInstance.post(
        "/cameras/refresh_frames/",
        {
          config_id: configId,
        },
        {
          timeout: timeoutMs,
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const retrieve_zones_by_config = async (config_id) => {
    try {
      const response = await axiosInstance.get(
        `/zone-configuration/retrieve-zones-by-config/`,
        {
          params: { config_id: config_id },
        }
      );
      const data = response.data;
      return data;
    } catch (error) {
      console.error("Erro ao carregar zonas:", error);
    }
  };

  const retrieve_lines_by_config = async (config_id) => {
    try {
      const response = await axiosInstance.get(
        `/zone-configuration/retrieve-lines-by-config/`,
        {
          params: { config_id: config_id },
        }
      );
      const data = response.data;
      return data;
    } catch (error) {
      console.error("Erro ao carregar zonas:", error);
    }
  };

  const getAccessLogs = async (params) => {
    try {
      const response = await axiosInstance.get("/access-logs/", { params });
      return response;
    } catch (error) {
      throw error;
    }
  };

  // ZonesCollections
  const getZoneActualCollection = async (zoneId) => {
    try {
      const response = await axiosInstance.get(
        `/zones-collections/${zoneId}/get_zone_actual_collection/`
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const patchZoneCollectionUnion = async (zoneId, params) => {
    try {
      const response = await axiosInstance.patch(
        `/zones-collections/${zoneId}/`,
        null,
        {
          params: params,
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const postZonesCollections = async (params) => {
    try {
      const response = await axiosInstance.post("/zones-collections/", null, {
        params: params,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const replaceZoneCollection = async (zoneId, data) => {
    const response = await axiosInstance.post(
      `/zones-collections/${zoneId}/replace/`,
      data
    );
    return response.data;
  };

  const getZoneCollectionsHistory = async (zoneId) => {
    try {
      const response = await axiosInstance.get(
        `/zones-collections/${zoneId}/history/`
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const patchZoneCollectionSegment = async (id, params) => {
    try {
      const response = await axiosInstance.patch(
        `/zones-collections/${id}/edit-segment/`,
        params
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const deleteZoneCollectionSegment = async (id, reason) => {
    try {
      const response = await axiosInstance.delete(`/zones-collections/${id}/`, {
        data: { reason },
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getCollectionActiveZones = async (legacyId) => {
    try {
      const response = await axiosInstance.get(
        "/zones-collections/get_collection_active_zones/",
        { params: { legacy_id: legacyId } }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const postStandardSchedule = async (data) => {
    try {
      const response = await axiosInstance.post("/standard-schedule/", data);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const postSpecialSchedule = async (data) => {
    try {
      const response = await axiosInstance.post("/special-schedule/", data);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const postRevenueGoal = async (data) => {
    try {
      const response = await axiosInstance.post("/revenue-goal/", data);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getRevenueGoals = async () => {
    try {
      const response = await axiosInstance.get("/revenue-goal/");
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const deleteRevenueGoal = async (id) => {
    try {
      await axiosInstance.delete(`/revenue-goal/${id}/`);
    } catch (error) {
      throw error;
    }
  };

  const getRevenueGoalSellers = async (month) => {
    try {
      const response = await axiosInstance.get(
        "/revenue-goal-seller/for_month/",
        {
          params: { month },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const bulkUpdateRevenueGoalSellers = async (month, sellers) => {
    try {
      const response = await axiosInstance.post(
        "/revenue-goal-seller/bulk_update/",
        {
          month,
          sellers,
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const getWeeklySchedule = async (date) => {
    try {
      const response = await axiosInstance.get(
        "/standard-schedule/get_branch_schedule/",
        {
          params: {
            date: date,
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const existsZoneWithoutCollection = async () => {
    try {
      const response = await axiosInstance.get(
        "/zones-collections/exists_zone_without_collection/"
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const existsDaysWithoutSchedule = async ({ storeId }) => {
    try {
      const response = await axiosInstance.get(
        "/standard-schedule/exists_days_without_schedule/",
        {
          params: {
            store: storeId,
          },
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getUnlinkedCollections = async () => {
    try {
      const response = await axiosInstance.get(
        "/home/check_unlinked_collections/"
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getCollectionsWithoutRevenue = async () => {
    try {
      const response = await axiosInstance.get(
        "/home/check_collections_without_revenue/"
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getBranchKpis = async (params) => {
    try {
      const response = await axiosInstance.get("/home/branch_kpis/", {
        params: params,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getGeneralRevenueGoalChart = async (params) => {
    try {
      const response = await axiosInstance.get(
        "/home/general_revenue_goal_chart/",
        {
          params: params,
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getGeneralSellerPerformance = async (params) => {
    try {
      const response = await axiosInstance.get(
        "/home/general_seller_performance/",
        { params }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getRScoreAvailableTipos = async () => {
    try {
      const response = await axiosInstance.get(
        "/rscore-benchmarks/available-tipos/"
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const getRScoreBenchmarks = async () => {
    try {
      const response = await axiosInstance.get("/rscore-benchmarks/");
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const createRScoreBenchmark = async (data) => {
    try {
      const response = await axiosInstance.post("/rscore-benchmarks/", data);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const updateRScoreBenchmark = async (id, data) => {
    try {
      const response = await axiosInstance.patch(
        `/rscore-benchmarks/${id}/`,
        data
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const deleteRScoreBenchmark = async (id) => {
    try {
      await axiosInstance.delete(`/rscore-benchmarks/${id}/`);
    } catch (error) {
      throw error;
    }
  };

  const getClients = async (includeInactive = false) => {
    try {
      const params = {};
      if (includeInactive) params.include_inactive = "true";
      const response = await axiosInstance.get("/client/", { params });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getBranches = async (clientId, includeInactive = false) => {
    try {
      const params = {};
      if (clientId) params.client_id = clientId;
      if (includeInactive) params.include_inactive = "true";
      const response = await axiosInstance.get("/branch/", { params });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const postClients = async (name, tipo) => {
    try {
      const response = await axiosInstance.post("/client/", {
        name,
        tipo: tipo || null,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const postBranch = async (
    branchName,
    clientId,
    storeCode,
    branchIP = null
  ) => {
    try {
      const payload = {
        name: branchName,
        client: clientId,
      };

      if (storeCode) {
        payload.store_code = storeCode;
      }

      if (branchIP) {
        payload.ip_address = branchIP;
      }

      const response = await axiosInstance.post("/branch/", payload);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const postUserBranch = async (userId, branchId) => {
    try {
      const response = await axiosInstance.post("/user-branch/", {
        user: userId,
        store: branchId,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const patchBranchData = async (id, updatedFields) => {
    try {
      const response = await axiosInstance.patch(
        `/branch/${id}/`,
        updatedFields
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const patchClientData = async (id, updatedFields) => {
    try {
      const response = await axiosInstance.patch(
        `/client/${id}/`,
        updatedFields
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const deleteClientData = async (id) => {
    try {
      const response = await axiosInstance.delete(`/client/${id}/`);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const deleteBranchData = async (id) => {
    try {
      const response = await axiosInstance.delete(`/branch/${id}/`);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const patchUserBranch = async ({ userId, branchIds, clientId = null }) => {
    try {
      const payload = {
        user_id: userId,
        branch_ids: branchIds,
      };
      if (clientId !== null) {
        payload.client_id = clientId;
      }

      const { data } = await axiosInstance.post("/user-branch/sync/", payload);
      return data;
    } catch (error) {
      throw error;
    }
  };

  // Jasper module management
  const getJasperModules = async (branchId) => {
    try {
      const response = await axiosInstance.get("/jasper/modules/", {
        params: { branch_id: branchId },
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getZonesModules = async (zoneId) => {
    try {
      const response = await axiosInstance.get("/jasper/zones-modules/", {
        params: { zone_id: zoneId },
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const postZonesModules = async ({ zoneId, moduleId, unionStartDatetime }) => {
    try {
      const response = await axiosInstance.post("/jasper/zones-modules/", {
        zone_id: zoneId,
        module_id: moduleId,
        union_start_datetime: unionStartDatetime,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const closeZonesModules = async (id) => {
    try {
      const response = await axiosInstance.post(
        `/jasper/zones-modules/${id}/close/`
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const patchZonesModuleSegment = async (id, params) => {
    try {
      const response = await axiosInstance.patch(
        `/jasper/zones-modules/${id}/edit/`,
        params
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const deleteZonesModule = async (id, reason) => {
    try {
      const response = await axiosInstance.delete(
        `/jasper/zones-modules/${id}/`,
        { data: { reason } }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  // ── Non-sale reasons ──────────────────────────────────────────────────────

  const getNonSaleReasons = async (clientId) => {
    try {
      const params = clientId ? { client_id: clientId } : {};
      const response = await axiosInstance.get("/non-sale-reason/", { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const postNonSaleReason = async (data) => {
    try {
      const response = await axiosInstance.post("/non-sale-reason/", data);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const patchNonSaleReason = async (id, data) => {
    try {
      const response = await axiosInstance.patch(
        `/non-sale-reason/${id}/`,
        data
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const deleteNonSaleReason = async (id) => {
    try {
      const response = await axiosInstance.delete(`/non-sale-reason/${id}/`);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // ── Lista da Vez ──────────────────────────────────────────────────────────

  const getTurnList = async (branchId) => {
    try {
      const response = await axiosInstance.get("/turn-list/", {
        params: { branch_id: branchId },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const getTurnListRevenue = async (branchId) => {
    try {
      const response = await axiosInstance.get("/turn-list/revenue/", {
        params: { branch_id: branchId },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const postTurnListRegister = async (payload) => {
    try {
      const response = await axiosInstance.post(
        "/turn-list/register/",
        payload
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const postTurnListPresence = async (payload) => {
    try {
      const response = await axiosInstance.post(
        "/turn-list/presence/",
        payload
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const postTurnListLineCut = async (payload) => {
    try {
      const response = await axiosInstance.post(
        "/turn-list/line_cut/",
        payload
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const postTurnListUndo = async (branchId, attendanceId) => {
    try {
      const response = await axiosInstance.post("/turn-list/undo/", {
        branch_id: branchId,
        attendance_id: attendanceId,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const patchTurnListPending = async (id, payload) => {
    try {
      const response = await axiosInstance.patch(
        `/turn-list/${id}/pending/`,
        payload
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // ── Lost sales analysis ───────────────────────────────────────────────────

  const getLostSalesSellers = async (branchId) => {
    try {
      const response = await axiosInstance.get("/lost-sales/sellers/", {
        params: { branch_id: branchId },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const postLostSalesRegister = async (payload) => {
    try {
      const response = await axiosInstance.post(
        "/lost-sales/register/",
        payload
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const getLostSalesFetchRegister = async (id) => {
    try {
      const response = await axiosInstance.get(
        `/lost-sales/${id}/fetch_register/`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const patchLostSalesRegister = async (id, payload) => {
    try {
      const response = await axiosInstance.patch(
        `/lost-sales/${id}/edit_register/`,
        payload
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const deleteLostSalesRegister = async (id) => {
    try {
      await axiosInstance.delete(`/lost-sales/${id}/delete_register/`);
    } catch (error) {
      throw error;
    }
  };

  const getManualProductAssignments = async (params) => {
    try {
      const response = await axiosInstance.get("/manual-product-assignments/", {
        params,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const getOuranCatalogPilot = async () => {
    const response = await axiosInstance.get("/ouran-catalog-pilot/");
    return response.data;
  };

  const reviewOuranCatalogProduct = async (data) => {
    const response = await axiosInstance.post(
      "/ouran-catalog-pilot/review/",
      data
    );
    return response.data;
  };

  const editOuranCatalogOverride = async (id, data) => {
    const response = await axiosInstance.patch(
      `/ouran-catalog-pilot/${id}/editar-ajuste/`,
      data
    );
    return response.data;
  };

  const deleteOuranCatalogOverride = async (id) => {
    const response = await axiosInstance.delete(
      `/ouran-catalog-pilot/${id}/excluir-ajuste/`
    );
    return response.data;
  };

  const createOuranCatalogCategory = async (data) => {
    const response = await axiosInstance.post(
      "/ouran-catalog-pilot/criar-categoria/",
      data
    );
    return response.data;
  };

  const generateOuranCatalogBatch = async (data) => {
    const response = await axiosInstance.post(
      "/ouran-catalog-pilot/gerar-lote/",
      data
    );
    return response.data;
  };

  // Lista paginada dos SKUs da filial, vinda do faturamento do período em vez
  // do lote. Substitui o snapshot como fonte da tela.
  const getOuranCatalogSkus = async (params) => {
    const response = await axiosInstance.get("/ouran-catalog-pilot/skus/", {
      params,
    });
    return response.data;
  };

  // Opções do filtro "Categoria ERP" (só as que venderam no período).
  const getOuranCatalogSkuFiltros = async (params) => {
    const response = await axiosInstance.get(
      "/ouran-catalog-pilot/skus/filtros/",
      { params }
    );
    return response.data;
  };

  const previewOuranCatalogOverride = async (data) => {
    const response = await axiosInstance.post(
      "/ouran-catalog-pilot/override-preview/",
      data
    );
    return response.data;
  };

  const saveOuranCatalogOverride = async (data) => {
    const response = await axiosInstance.post(
      "/ouran-catalog-pilot/override/",
      data
    );
    return response.data;
  };

  const getOuranCatalogOverrideHistory = async (params) => {
    const response = await axiosInstance.get(
      "/ouran-catalog-pilot/override-history/",
      { params }
    );
    return response.data;
  };

  const getOuranCatalogModePreview = async () => {
    const response = await axiosInstance.get(
      "/ouran-catalog-pilot/modo-preview/"
    );
    return response.data;
  };

  const activateOuranCatalogMode = async (data) => {
    const response = await axiosInstance.post(
      "/ouran-catalog-pilot/ativar-modo/",
      data
    );
    return response.data;
  };

  // Vínculos zona–categoria no modo Ouran usam category_id, e não o
  // legacy_id das rotas zones-collections.
  const getOuranZoneCategories = async () => {
    const response = await axiosInstance.get(
      "/zone-ouran-categories/categories/"
    );
    return response.data;
  };

  const getOuranZoneCategoryHistory = async (zoneId) => {
    const response = await axiosInstance.get(
      "/zone-ouran-categories/history/",
      { params: { zone_id: zoneId } }
    );
    return response.data;
  };

  const getOuranZoneCategoryActiveZones = async (categoryId) => {
    const response = await axiosInstance.get(
      "/zone-ouran-categories/active-zones/",
      { params: { category_id: categoryId } }
    );
    return response.data;
  };

  const createOuranZoneCategory = async (data) => {
    const response = await axiosInstance.post("/zone-ouran-categories/", data);
    return response.data;
  };

  const closeOuranZoneCategory = async (id, data) => {
    const response = await axiosInstance.patch(
      `/zone-ouran-categories/${id}/close/`,
      data
    );
    return response.data;
  };

  const replaceOuranZoneCategory = async (id, data) => {
    const response = await axiosInstance.post(
      `/zone-ouran-categories/${id}/replace/`,
      data
    );
    return response.data;
  };

  const editOuranZoneCategorySegment = async (id, data) => {
    const response = await axiosInstance.patch(
      `/zone-ouran-categories/${id}/edit-segment/`,
      data
    );
    return response.data;
  };

  const deleteOuranZoneCategorySegment = async (id, reason) => {
    const response = await axiosInstance.delete(
      `/zone-ouran-categories/${id}/`,
      { data: { reason } }
    );
    return response.data;
  };

  const saveManualProductAssignments = async (assignments) => {
    try {
      const response = await axiosInstance.post(
        "/manual-product-assignments/save/",
        { assignments }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const removeManualProductAssignment = async (produto) => {
    try {
      const response = await axiosInstance.post(
        "/manual-product-assignments/remove/",
        { produto }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const addManualCollection = async (description) => {
    try {
      const response = await axiosInstance.post(
        "/manual-product-assignments/add_collection/",
        { description }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const getSkuCollectionHistory = async (produto) => {
    try {
      const response = await axiosInstance.get(
        "/manual-product-assignments/history/",
        { params: { produto } }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const patchSkuCollectionSegment = async (id, params) => {
    try {
      const response = await axiosInstance.patch(
        `/manual-product-assignments/${id}/edit-segment/`,
        params
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const deleteSkuCollectionSegment = async (id, reason) => {
    try {
      const response = await axiosInstance.delete(
        `/manual-product-assignments/${id}/`,
        { data: { reason } }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const deleteManualCollection = async (legacy_id) => {
    try {
      const response = await axiosInstance.post(
        "/manual-product-assignments/delete_collection/",
        { legacy_id }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const getCamerasMonitoring = async (excludeClient = null) => {
    try {
      const params = excludeClient ? { exclude_client: excludeClient } : {};
      const response = await axiosInstance.get("/camera-monitoring/", {
        params,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getCameraMonitoringHistory = async (cameraId, days = 30) => {
    try {
      const response = await axiosInstance.get(
        `/camera-monitoring/${cameraId}/history/`,
        { params: { days } }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getAiReportsCatalog = async () => {
    const response = await axiosInstance.get("/v2/ai-reports/catalog/");
    return response.data;
  };

  const getAiReportRoutines = async (params) => {
    const response = await axiosInstance.get("/v2/ai-reports/routines/", {
      params,
    });
    return response.data;
  };

  const restoreAiReportRoutine = async (id) => {
    const response = await axiosInstance.post(
      `/v2/ai-reports/routines/${id}/restaurar/`
    );
    return response.data;
  };

  const createAiReportRoutine = async (data) => {
    const response = await axiosInstance.post("/v2/ai-reports/routines/", data);
    return response.data;
  };

  const updateAiReportRoutine = async (id, data) => {
    const response = await axiosInstance.patch(
      `/v2/ai-reports/routines/${id}/`,
      data
    );
    return response.data;
  };

  const deleteAiReportRoutine = async (id) => {
    const response = await axiosInstance.delete(
      `/v2/ai-reports/routines/${id}/`
    );
    return response.data;
  };

  const getAiReportDelivery = async () => {
    const response = await axiosInstance.get("/v2/ai-reports/entrega/");
    return response.data;
  };

  const updateAiReportDelivery = async (data) => {
    const response = await axiosInstance.put("/v2/ai-reports/entrega/", data);
    return response.data;
  };

  const getAiReportsConfig = async () => {
    const response = await axiosInstance.get("/v2/ai-reports/configuracao/");
    return response.data;
  };

  const updateAiReportsConfig = async (data) => {
    const response = await axiosInstance.put(
      "/v2/ai-reports/configuracao/",
      data
    );
    return response.data;
  };

  const planAiReport = async (data) => {
    const response = await axiosInstance.post("/v2/ai-reports/planejar/", data);
    return response.data;
  };

  const sampleAiReportVersion = async (id, data = {}) => {
    const response = await axiosInstance.post(
      `/v2/ai-reports/versions/${id}/amostra/`,
      data
    );
    return response.data;
  };

  const getAiReportRuns = async (params) => {
    const response = await axiosInstance.get("/v2/ai-reports/runs/", {
      params,
    });
    return response.data;
  };

  const getAiReportRun = async (id) => {
    const response = await axiosInstance.get(`/v2/ai-reports/runs/${id}/`);
    return response.data;
  };

  const createAiReportRuns = async (data) => {
    const response = await axiosInstance.post("/v2/ai-reports/runs/", data);
    return response.data;
  };

  const cancelAiReportRun = async (id) => {
    const response = await axiosInstance.post(
      `/v2/ai-reports/runs/${id}/cancel/`
    );
    return response.data;
  };

  const deliverAiReportRun = async (id, data = {}) => {
    const response = await axiosInstance.post(
      `/v2/ai-reports/runs/${id}/deliver/`,
      data
    );
    return response.data;
  };

  const sendAgentMessage = async ({ branch_id, texto }) => {
    const response = await axiosInstance.post("/v2/agent/messages/", {
      branch_id,
      texto,
    });
    return response.data;
  };

  const getAgentMessage = async (id) => {
    const response = await axiosInstance.get(`/v2/agent/messages/${id}/`);
    return response.data;
  };

  const getAgentStatus = async () => {
    const response = await axiosInstance.get("/v2/agent/status/");
    return response.data;
  };

  const getAgentConversation = async (branch_id) => {
    const response = await axiosInstance.get(
      "/v2/agent/conversations/current/",
      { params: { branch_id } }
    );
    return response.data;
  };

  const closeAgentConversation = async (id) => {
    const response = await axiosInstance.post(
      `/v2/agent/conversations/${id}/close/`
    );
    return response.data;
  };

  const confirmAgentAction = async (id) => {
    const response = await axiosInstance.post(
      `/v2/agent/actions/${id}/confirm/`
    );
    return response.data;
  };

  const cancelAgentAction = async (id) => {
    const response = await axiosInstance.post(
      `/v2/agent/actions/${id}/cancel/`
    );
    return response.data;
  };

  const getAgentConfig = async () => {
    const response = await axiosInstance.get("/v2/agent/gestao/config/");
    return response.data;
  };

  const updateAgentConfig = async (data) => {
    const response = await axiosInstance.put("/v2/agent/gestao/config/", data);
    return response.data;
  };

  const getAgentCatalog = async () => {
    const response = await axiosInstance.get("/v2/agent/gestao/catalogo/");
    return response.data;
  };

  const getAgentConversations = async (params) => {
    const response = await axiosInstance.get("/v2/agent/gestao/conversas/", {
      params,
    });
    return response.data;
  };

  const getAgentConversationDetail = async (id) => {
    const response = await axiosInstance.get(
      `/v2/agent/gestao/conversas/${id}/`
    );
    return response.data;
  };

  const getAgentPrompt = async () => {
    const response = await axiosInstance.get("/v2/agent/gestao/prompt/");
    return response.data;
  };

  const getAgentPromptVersion = async (numero) => {
    const response = await axiosInstance.get(
      `/v2/agent/gestao/prompt/${numero}/`
    );
    return response.data;
  };

  const createAgentPromptVersion = async (data) => {
    const response = await axiosInstance.post("/v2/agent/gestao/prompt/", data);
    return response.data;
  };

  const activateAgentPromptVersion = async (numero) => {
    const response = await axiosInstance.post(
      `/v2/agent/gestao/prompt/${numero}/ativar/`
    );
    return response.data;
  };

  const getAgentWhatsapp = async () => {
    const response = await axiosInstance.get("/v2/agent/gestao/whatsapp/");
    return response.data;
  };

  const updateAgentWhatsapp = async (data) => {
    const response = await axiosInstance.put(
      "/v2/agent/gestao/whatsapp/",
      data
    );
    return response.data;
  };

  const generateAgentWhatsappToken = async () => {
    const response = await axiosInstance.post(
      "/v2/agent/gestao/whatsapp/token/"
    );
    return response.data;
  };

  const getAgentPhones = async (params) => {
    const response = await axiosInstance.get("/v2/agent/gestao/telefones/", {
      params,
    });
    return response.data;
  };

  const createAgentPhone = async (data) => {
    const response = await axiosInstance.post(
      "/v2/agent/gestao/telefones/",
      data
    );
    return response.data;
  };

  const updateAgentPhone = async (id, data) => {
    const response = await axiosInstance.patch(
      `/v2/agent/gestao/telefones/${id}/`,
      data
    );
    return response.data;
  };

  const deleteAgentPhone = async (id) => {
    await axiosInstance.delete(`/v2/agent/gestao/telefones/${id}/`);
  };

  const getAgentUsage = async (params) => {
    const response = await axiosInstance.get("/v2/agent/gestao/resumo/", {
      params,
    });
    return response.data;
  };

  const getGatewayFiliais = async () => {
    const response = await axiosInstance.get("/gateway/gestao/filiais/");
    return response.data;
  };

  const getGatewayFilial = async (filialId) => {
    const response = await axiosInstance.get(
      `/gateway/gestao/filiais/${filialId}/`
    );
    return response.data;
  };

  const updateGatewayParametros = async (filialId, data) => {
    const response = await axiosInstance.patch(
      `/gateway/gestao/filiais/${filialId}/`,
      data
    );
    return response.data;
  };

  const generateGatewayToken = async (filialId) => {
    const response = await axiosInstance.post(
      `/gateway/gestao/filiais/${filialId}/token/`
    );
    return response.data;
  };

  const revokeGatewayToken = async (filialId) => {
    await axiosInstance.delete(`/gateway/gestao/filiais/${filialId}/token/`);
  };

  const decideGatewayGravacao = async (filialId, parada) => {
    const response = await axiosInstance.post(
      `/gateway/gestao/filiais/${filialId}/gravacao/`,
      { parada }
    );
    return response.data;
  };

  const createGatewayCamera = async (filialId, data) => {
    const response = await axiosInstance.post(
      `/gateway/gestao/filiais/${filialId}/cameras/`,
      data
    );
    return response.data;
  };

  const getGatewayCamera = async (filialId, cameraId) => {
    const response = await axiosInstance.get(
      `/gateway/gestao/filiais/${filialId}/cameras/${cameraId}/`
    );
    return response.data;
  };

  const updateGatewayCamera = async (filialId, cameraId, data) => {
    const response = await axiosInstance.patch(
      `/gateway/gestao/filiais/${filialId}/cameras/${cameraId}/`,
      data
    );
    return response.data;
  };

  const deleteGatewayCamera = async (filialId, cameraId) => {
    const response = await axiosInstance.delete(
      `/gateway/gestao/filiais/${filialId}/cameras/${cameraId}/`
    );
    return response.data;
  };

  const decideGatewayCameraGravacao = async (filialId, cameraId, parada) => {
    const response = await axiosInstance.post(
      `/gateway/gestao/filiais/${filialId}/cameras/${cameraId}/gravacao/`,
      { parada }
    );
    return response.data;
  };

  return {
    getTestConnection,
    getGatewayFiliais,
    getGatewayFilial,
    updateGatewayParametros,
    generateGatewayToken,
    revokeGatewayToken,
    decideGatewayGravacao,
    createGatewayCamera,
    getGatewayCamera,
    updateGatewayCamera,
    deleteGatewayCamera,
    decideGatewayCameraGravacao,
    getCollections,
    getZones,
    getLines,
    getZoneConfigs,
    createZoneConfig,
    updateZoneConfigs,
    patchZoneIsStaff,
    patchZoneIsFila,
    updateZoneCoordinates,
    updateLineCoordinates,
    deactivateOtherConfigs,
    removeZoneFromConfig,
    removeLineFromConfig,
    deleteLine,
    duplicateConfig,
    getCalibrationStatus,
    getFlowDetectionsLastRecord,
    getEventsLastChange,
    postEvent,
    patchEvent,
    deleteEvent,
    updateEventGroup,
    deleteEventGroup,
    getEventsList,
    getZoneById,
    patchZoneCollection,
    postCollection,
    postConfigs,
    getCheckEventsContent,
    getCheckCollectionsChangeContent,
    getCheckFlowDetectionsContent,
    postZones,
    postLines,
    getCameras,
    countCameras,
    refreshConfigCameraFrames,
    updateZonesInCameras,
    updateLinesInCameras,
    getAccessLogs,
    createCamera,
    removeCamera,
    editCamera,
    setActiveCamera,
    getCameraEnablementHistory,
    retrieve_zones_by_config,
    retrieve_lines_by_config,
    getZoneActualCollection,
    getZoneCollectionsHistory,
    patchZoneCollectionUnion,
    patchZoneCollectionSegment,
    replaceZoneCollection,
    deleteZoneCollectionSegment,
    postZonesCollections,
    getCollectionActiveZones,
    postStandardSchedule,
    postSpecialSchedule,
    postRevenueGoal,
    getWeeklySchedule,
    getRevenueGoals,
    deleteRevenueGoal,
    getRevenueGoalSellers,
    bulkUpdateRevenueGoalSellers,
    existsZoneWithoutCollection,
    existsDaysWithoutSchedule,
    getUnlinkedCollections,
    getCollectionsWithoutRevenue,
    getBranchKpis,
    getGeneralRevenueGoalChart,
    getGeneralSellerPerformance,
    getRScoreAvailableTipos,
    getRScoreBenchmarks,
    createRScoreBenchmark,
    updateRScoreBenchmark,
    deleteRScoreBenchmark,
    getClients,
    getBranches,
    postUserBranch,
    postClients,
    postBranch,
    patchBranchData,
    patchClientData,
    deleteClientData,
    deleteBranchData,
    patchUserBranch,
    getJasperModules,
    getZonesModules,
    postZonesModules,
    closeZonesModules,
    patchZonesModuleSegment,
    deleteZonesModule,
    getNonSaleReasons,
    postNonSaleReason,
    patchNonSaleReason,
    deleteNonSaleReason,
    getTurnList,
    getTurnListRevenue,
    postTurnListRegister,
    postTurnListPresence,
    postTurnListLineCut,
    postTurnListUndo,
    patchTurnListPending,
    getLostSalesSellers,
    postLostSalesRegister,
    getLostSalesFetchRegister,
    patchLostSalesRegister,
    deleteLostSalesRegister,
    getManualProductAssignments,
    getOuranCatalogPilot,
    reviewOuranCatalogProduct,
    generateOuranCatalogBatch,
    getOuranCatalogSkus,
    getOuranCatalogSkuFiltros,
    createOuranCatalogCategory,
    editOuranCatalogOverride,
    deleteOuranCatalogOverride,
    previewOuranCatalogOverride,
    saveOuranCatalogOverride,
    getOuranCatalogOverrideHistory,
    getOuranCatalogModePreview,
    activateOuranCatalogMode,
    getOuranZoneCategories,
    getOuranZoneCategoryHistory,
    getOuranZoneCategoryActiveZones,
    createOuranZoneCategory,
    closeOuranZoneCategory,
    replaceOuranZoneCategory,
    editOuranZoneCategorySegment,
    deleteOuranZoneCategorySegment,
    saveManualProductAssignments,
    removeManualProductAssignment,
    addManualCollection,
    getSkuCollectionHistory,
    patchSkuCollectionSegment,
    deleteSkuCollectionSegment,
    deleteManualCollection,
    getCamerasMonitoring,
    getCameraMonitoringHistory,
    getAiReportsCatalog,
    getAiReportRoutines,
    createAiReportRoutine,
    updateAiReportRoutine,
    deleteAiReportRoutine,
    restoreAiReportRoutine,
    planAiReport,
    getAiReportDelivery,
    updateAiReportDelivery,
    getAiReportsConfig,
    updateAiReportsConfig,
    sampleAiReportVersion,
    getAiReportRuns,
    getAiReportRun,
    createAiReportRuns,
    cancelAiReportRun,
    deliverAiReportRun,
    sendAgentMessage,
    getAgentMessage,
    getAgentStatus,
    getAgentConversation,
    closeAgentConversation,
    confirmAgentAction,
    cancelAgentAction,
    getAgentConfig,
    updateAgentConfig,
    getAgentCatalog,
    getAgentConversations,
    getAgentConversationDetail,
    getAgentUsage,
    getAgentPrompt,
    getAgentPromptVersion,
    createAgentPromptVersion,
    activateAgentPromptVersion,
    getAgentWhatsapp,
    updateAgentWhatsapp,
    generateAgentWhatsappToken,
    getAgentPhones,
    createAgentPhone,
    updateAgentPhone,
    deleteAgentPhone,
  };
};

export default useApi;
